import { useMemo, useState } from "react";

const button = "rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50";
const primary = "rounded-lg bg-[#1C64B0] px-4 py-2 text-sm font-semibold text-white hover:bg-[#154E8A] disabled:opacity-50";
const sel = "rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800";
const toNum = (v) => Number(String(v).replace(",", ".")) || 0;

export const GrondVerwerken = ({ xl }) => {
  const { grondLedger, grondBatchOpties, verwerkGrondstof, kgf } = xl;
  const [naam, setNaam] = useState("");
  const [partijId, setPartijId] = useState("");
  const [ingezet, setIngezet] = useState("");
  const [datum, setDatum] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([{ naam: "", kg: "" }]);

  // Alleen grondstoffen die nú op voorraad staan tonen (niet de hele stamlijst).
  const voorraadMat = useMemo(() => new Set((grondLedger.lots || []).filter((l) => l.rest > 0.001).map((l) => l.material)), [grondLedger.lots]);
  const groups = useMemo(() => grondLedger.catalog.filter((g) => g.naam && voorraadMat.has(g.material)).sort((a, b) => String(a.naam).localeCompare(String(b.naam), "nl")), [grondLedger.catalog, voorraadMat]);
  const alleGrondNamen = useMemo(() => [...new Set(grondLedger.catalog.map((g) => g.naam).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b), "nl")), [grondLedger.catalog]);
  const partijen = useMemo(() => (naam ? (grondBatchOpties(naam) || []).filter((p) => p.rest > 0.001) : []), [naam, grondBatchOpties]);
  const partij = partijen.find((p) => String(p.id) === String(partijId));

  const kiesNaam = (v) => { setNaam(v); setPartijId(""); setIngezet(""); };
  const kiesPartij = (id) => { setPartijId(id); const p = partijen.find((x) => String(x.id) === String(id)); setIngezet(p ? String(Math.round(p.rest * 1000) / 1000) : ""); };
  const setRow = (i, f, v) => setRows((l) => l.map((r, idx) => (idx === i ? { ...r, [f]: v } : r)));
  const addRow = () => setRows((l) => [...l, { naam: "", kg: "" }]);
  const delRow = (i) => setRows((l) => (l.length > 1 ? l.filter((_, idx) => idx !== i) : l));

  const inKg = toNum(ingezet);
  const uit = rows.reduce((s, r) => s + toNum(r.kg), 0);
  const verlies = Math.max(0, Math.round((inKg - uit) * 1000) / 1000);
  const teveel = uit > inKg + 0.001;
  const teveelIngezet = !!partij && inKg > (partij.rest + 0.001);

  const opslaan = () => {
    const ok = verwerkGrondstof({ bronNaam: naam, partij, datum, ingezet: inKg, outputs: rows });
    if (ok === true) { setRows([{ naam: "", kg: "" }]); setPartijId(""); setNaam(""); setIngezet(""); }
  };

  return (
    <section data-testid="vg-verwerk" className="rounded-xl border border-slate-200 bg-white p-5 mb-6">
      <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-400">Verwerken &amp; uitsplitsen</div>
      <p className="mb-4 max-w-3xl text-sm text-slate-500">Splits een partij grondstof (bijv. ontdooide filet) op in meerdere onderdelen (bijv. schnitzels, afsnijdsels). De onderdelen komen als <b>grondstof</b> terug in je voorraad — met de tracecode van de bronpartij — zodat je ze daarna kunt aanvinken als grondstof in een halffabrikaat. Wat overblijft wordt automatisch als <b>vochtverlies</b> afgeboekt, zodat je tracering volledig rond is.</p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs font-medium text-slate-600"><span>Grondstof (op voorraad)</span>
          <select data-testid="vg-verwerk-naam" className={`${sel} w-64`} value={naam} onChange={(e) => kiesNaam(e.target.value)}>
            <option value="">Kies grondstof…</option>
            {groups.map((g) => <option key={g.material || g.naam} value={g.naam}>{g.artikelnummer ? `${g.artikelnummer} · ` : ""}{g.naam}</option>)}
          </select>
        </label>
        {naam && (
          <label className="flex flex-col gap-1 text-xs font-medium text-slate-600"><span>Partij / tracecode</span>
            <select data-testid="vg-verwerk-partij" className={`${sel} w-72`} value={partijId} onChange={(e) => kiesPartij(e.target.value)}>
              <option value="">Kies partij…</option>
              {partijen.map((p) => <option key={p.id} value={p.id}>{`${p.eigenTracecode || p.batchnr || "zonder tracecode"} · ${kgf(p.rest)}${p.datum ? ` · ${p.datum}` : ""}`}</option>)}
            </select>
          </label>
        )}
        {partij && (
          <label className="flex flex-col gap-1 text-xs font-medium text-slate-600"><span>Ingezet kg</span>
            <input data-testid="vg-verwerk-ingezet" inputMode="decimal" className={`${sel} w-28`} value={ingezet} onChange={(e) => setIngezet(e.target.value)} />
          </label>
        )}
        {partij && (
          <label className="flex flex-col gap-1 text-xs font-medium text-slate-600"><span>Datum</span>
            <input type="date" data-testid="vg-verwerk-datum" className={sel} value={datum} onChange={(e) => setDatum(e.target.value)} />
          </label>
        )}
      </div>

      {partij && (
        <div className="mt-4 space-y-2">
          <div className="text-xs font-semibold text-slate-500">Onderdelen (grondstof) — kies een bestaande grondstof of typ een nieuwe naam</div>
          <datalist id="vg-grond-namen">{alleGrondNamen.map((n) => <option key={n} value={n} />)}</datalist>
          {rows.map((r, i) => (
            <div key={i} data-testid={`vg-verwerk-row-${i}`} className="flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1 text-xs font-medium text-slate-600"><span>Naam grondstof</span>
                <input list="vg-grond-namen" data-testid={`vg-verwerk-out-naam-${i}`} className={`${sel} w-64`} placeholder="bijv. Schnitzels van filet" value={r.naam} onChange={(e) => setRow(i, "naam", e.target.value)} />
              </label>
              <label className="flex flex-col gap-1 text-xs font-medium text-slate-600"><span>kg{inKg > 0 && toNum(r.kg) > 0 ? ` · ${Math.round((toNum(r.kg) / inKg) * 1000) / 10}%` : ""}</span>
                <input data-testid={`vg-verwerk-out-kg-${i}`} inputMode="decimal" className={`${sel} w-28`} placeholder="kg" value={r.kg} onChange={(e) => setRow(i, "kg", e.target.value)} />
              </label>
              <button data-testid={`vg-verwerk-del-${i}`} className={button} disabled={rows.length === 1} onClick={() => delRow(i)}>Verwijder</button>
            </div>
          ))}
          <button data-testid="vg-verwerk-add" className={button} onClick={addRow}>+ Onderdeel toevoegen</button>

          <div data-testid="vg-verwerk-balans" className={`mt-2 rounded-lg border p-3 text-sm ${teveel || teveelIngezet ? "border-rose-200 bg-rose-50 text-rose-800" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
            {teveelIngezet ? <span className="font-semibold">Je zet meer in ({kgf(inKg)}) dan er op deze partij staat ({kgf(partij.rest)}). Verlaag de ingezette kg.</span> : <>Ingezet <b>{kgf(inKg)}</b> · onderdelen <b>{kgf(uit)}</b> · {teveel ? <span className="font-semibold">te veel uitgesplitst — verlaag de kg</span> : <>vochtverlies <b data-testid="vg-verwerk-verlies">{kgf(verlies)}</b>{inKg > 0 ? ` (${Math.round((verlies / inKg) * 1000) / 10}%)` : ""} wordt automatisch afgeboekt</>}</>}
          </div>
          <button data-testid="vg-verwerk-save" className={`${primary} mt-1`} disabled={teveel || teveelIngezet || uit <= 0} onClick={opslaan}>Verwerken &amp; afboeken</button>
        </div>
      )}
    </section>
  );
};
